// NestedTreeRows — a reusable, one-level nested tree that draws SolidWorks-style
// connector lines identical to the Files/Folders sidebar
// (`src/views/files/FilesSidebar.tsx` → `renderFolderTree`).
//
// It renders a single top-level PARENT row (named after the feature/domain) and
// NESTS its child rows one level beneath it, with the ├─ / └─ vertical + elbow
// guide lines and the same indentation the Files sidebar uses. There is no
// checkbox glyph — a child row is just its label, indented under the elbow.
//
// The connector geometry is copied verbatim from FilesSidebar so the two trees
// look pixel-identical:
//   lineX(d)   = 38 + (d - 1) * 12   — x-center of the connector for depth d
//   ROW_MID    = 16                  — vertical mid of a row (where the elbow sits)
//   rowPadLeft = 32 + depth * 12     — left padding of the row's own content
//   LINE_COLOR = #404040
//
// This is intentionally generic (no feature-window coupling) so the JAWS
// "Features" list in SuspensionKinematics.tsx can reuse the exact same pattern —
// pass it a parent label + a flat list of leaf rows.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** One nested leaf row (a child of the top-level parent). */
export interface NestedTreeRow {
  /** Stable React key. */
  key: string;
  /** Visible label. */
  label: string;
  /** Whether the row reads as "active"/open (tints the label green). */
  active?: boolean;
  /** Click handler (e.g. toggle the feature window open/closed). */
  onClick?: () => void;
}

export interface NestedTreeRowsProps {
  /** The top-level parent row's label (the feature / domain name). */
  parentLabel: string;
  /** Optional icon rendered to the left of the parent label. */
  parentIcon?: ReactNode;
  /** The nested child rows drawn one level beneath the parent. */
  rows: NestedTreeRow[];
  /** Extra classes for the outer wrapper. */
  className?: string;
}

// Connector geometry — kept identical to FilesSidebar.renderFolderTree so both
// trees render the same guide lines.
const LINE_COLOR = '#404040';
const ROW_MID = 16; // vertical mid of a row (elbow height)
const lineX = (depth: number) => 38 + (depth - 1) * 12; // spine x for a depth
const rowPadLeft = (depth: number) => 32 + depth * 12; // row content indent

/**
 * A single-parent, one-level nested tree with Files-sidebar connector lines.
 *
 * Reuse: pass a `parentLabel` + a flat `rows` array. Each row's `onClick`
 * fires on click; `active` tints it. Drop-in for any "domain → items" list that
 * wants the SolidWorks/Files-style ├─└─ nesting (no checkbox glyph).
 */
export function NestedTreeRows({
  parentLabel,
  parentIcon,
  rows,
  className,
}: NestedTreeRowsProps) {
  if (rows.length === 0) return null;

  return (
    <div className={cn('py-1.5', className)}>
      {/* Top-level parent row (depth 0) — named after the feature. */}
      <div
        className="flex items-center gap-2 py-1.5 text-xs font-medium text-telekinesis-neutral-50"
        style={{ paddingLeft: rowPadLeft(0) }}
      >
        {parentIcon}
        <span className="min-w-0 flex-1 truncate">{parentLabel}</span>
      </div>

      {/* Nested child rows (depth 1) with connector lines. */}
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1;
        return (
          <div key={row.key} className="relative">
            {/* Current-level vertical segment: full height for a non-last
                child, half-row (down to the elbow) for the last child. */}
            <div
              className="pointer-events-none absolute z-[1]"
              style={{
                left: lineX(1),
                width: 1,
                top: 0,
                ...(isLast ? { height: ROW_MID } : { bottom: 0 }),
                background: LINE_COLOR,
              }}
            />
            {/* Horizontal arm from the spine to the row content. */}
            <div
              className="pointer-events-none absolute z-[1]"
              style={{
                left: lineX(1),
                top: ROW_MID,
                width: rowPadLeft(1) - lineX(1),
                height: 1,
                background: LINE_COLOR,
              }}
            />
            <button
              type="button"
              onClick={row.onClick}
              className={cn(
                'flex w-full items-center gap-2 py-1.5 pr-3 text-left text-xs transition-colors hover:bg-telekinesis-bg-dark/50',
                row.active ? 'text-[#107d40]' : 'text-telekinesis-neutral-50',
              )}
              style={{ paddingLeft: rowPadLeft(1) }}
            >
              <span className="min-w-0 flex-1 truncate">{row.label}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
